import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoMedia from "@/components/PhotoMedia";
import UnlockPaymentModal from "@/components/UnlockPaymentModal";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Bookmark, Play, Unlock } from "lucide-react";
import { euro } from "@/lib/format";
import { toast } from "sonner";

const Favorites = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [filter, setFilter] = useState("all"); // all | photo | video

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/saved");
      setPosts(data.posts);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = posts.filter((p) => filter === "all" ? true : (p.media_type || "photo") === filter);

  const openUnlock = (post) => { setSelected(post); setModalOpen(true); };
  const confirmUnlock = async (post) => {
    try {
      const { data } = await api.post(`/photos/${post.id}/purchase`);
      setUser(data.user);
      toast.success("Déverrouillé");
      await load();
    } catch (e) { toast.error(apiError(e)); throw e; }
  };

  const FILTERS = [
    { key: "all", label: "Tout" },
    { key: "photo", label: "Photos" },
    { key: "video", label: "Vidéos" },
  ];

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl flex items-center gap-2"><Bookmark className="h-6 w-6 text-primary" /> Mes favoris</h1>

      <div className="flex gap-2" data-testid="favorites-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`favorites-filter-${f.key}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${filter === f.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="aspect-[4/5] rounded-2xl" />)}</div>
      ) : shown.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><Bookmark className="h-6 w-6" /></div>
          <p className="font-medium text-foreground">Aucun favori</p>
          <p className="text-sm mt-1">Enregistrez des photos et vidéos pour les retrouver ici.</p>
          <Button className="mt-4 rounded-xl" onClick={() => navigate("/decouvrir")} data-testid="favorites-goto-feed">Explorer le feed</Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3" data-testid="favorites-grid">
          {shown.map((p) => {
            const unlocked = p.status === "unlocked" || p.is_sender || p.purchased;
            return (
              <div key={p.id} className="rounded-2xl overflow-hidden border border-border bg-card relative">
                <div className="relative aspect-[4/5]">
                  <PhotoMedia photo={p} />
                  {p.media_type === "video" && (
                    <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-black/55 px-2 py-0.5 text-[10px] text-white"><Play className="h-3 w-3 fill-white" /> Vidéo</div>
                  )}
                  {!unlocked && (
                    <button onClick={() => openUnlock(p)} className="absolute inset-0 grid place-items-center" data-testid={`favorite-unlock-${p.id}`}>
                      <span className="rounded-full bg-primary text-primary-foreground px-3 py-1.5 text-xs font-semibold flex items-center gap-1"><Unlock className="h-3.5 w-3.5" /> {euro(p.price)}</span>
                    </button>
                  )}
                </div>
                <button onClick={() => navigate(`/c/${p.sender?.username}`)} className="block w-full text-left p-2.5">
                  <p className="text-xs truncate">@{p.sender?.username}</p>
                  {p.caption && <p className="text-[11px] text-muted-foreground truncate">{p.caption}</p>}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <UnlockPaymentModal open={modalOpen} onOpenChange={setModalOpen} photo={selected} balance={user?.wallet_balance || 0} onConfirm={confirmUnlock} onRecharge={() => navigate("/portefeuille")} />
    </div>
  );
};

export default Favorites;
